import { h, Component } from 'preact';
import Store from 'store/store';
import * as actions from 'store/actions';
import { isDirectTicketingEnabled } from 'store/ticketingPublic';
import pure from 'utils/pure';
import { accessNested, joinClasses, textToJsxArray, isObject } from 'utils/utils';
import { trackNearestShowtimeTicketClick } from 'utils/clientUtils';
import formatDateTime from 'utils/formatDateTime';
import { track, updateDataLayerScreening } from 'utils/tracking';
import { Link } from 'components/core/link/link';
import s from 'components/shared/theaters/providerInterstitial.sss';


/**
 * The ProviderInterstitial the component that let you choose a ticket provider.
 * Common tickets providers are : 'cinema' (direct from theater), 'fandango', 'movietickets'
 * and 'atom'.
 */
export default @pure class ProviderInterstitial extends Component {

	/**
	 * Go back to time selection.
	 */
	back = () => {
		this.props.theater.set({ activeScreening: null });
		track({ a: 'close_provider_interstitial', useCustomPageTrack: true });
	}

	/**
	 * Go to our in-house ticketing.
	 */
	selectDirectTicketing = () => {
		const theater = this.props.theater;
		Store.emit(actions.OPEN_TICKETING, { theater: theater, screening: theater.activeScreening });
	}

	/**
	 * Redirect to the given ticker provider.
	 *
	 * @param {string} provider - the provider identifier
	 * @param {boolean} offer - whether the user was shown an offer
	 * @param {string} url - the provider url
	 * @param {MouseEvent} event - the mouse click event
	 * @return {boolean} always true
	 */
	selectProvider(provider, offer, url, event) {
		const { activeScreening, theater, pageData } = this.props;

		let cta;
		if (isObject(pageData.ctaCopy)) {
			const ctaVal = pageData.ctaCopy[activeScreening.cta];
			const copy = this.props.appData.copy;
			if (isObject(copy)) {
				cta = copy[ctaVal] || ctaVal;
			}
		}

		updateDataLayerScreening(activeScreening, theater, 'ticket_click', url, cta, provider);
		track({ a: 'ticket_click', l: activeScreening.id, useCustomPageTrack: true });

		const providers = (activeScreening && Object.keys(activeScreening.urls)) || [];
		if (provider) {
			track({ a: 'selected_ticket_provider', l: provider, useCustomPageTrack: true });
			if (offer) {
				track({ a: 'selected_ticket_provider_with_offer', l: provider, useCustomPageTrack: true });
			}

			const val = providers.map(p => {
				const active = p === provider;
				if (p === 'cinema' && theater.exhibitor) {
					p += ':' + theater.exhibitor.domain.slice(0,50);
				}
				return active ? '*' + p : p;
			}).join('|').slice(0, 250);

			track({ a: 'selected_ticket_provider_from_choices', l: val, useCustomPageTrack: true });
			if (pageData.customInterstitialTracking) {
				track({ a: pageData.customInterstitialTracking, l: val });
			}
		}

		trackNearestShowtimeTicketClick(theater);

		Store.emit(actions.TICKET_LINK_CLICKED, activeScreening, theater, event, provider);


		return true;
	}


	/**
	 * Return an array of provider names available for the active screening.
	 * Available providers are taken from `Screening.urls`
	 * The order is taken from `pageData.ticketProvidersOrder` but can be overriden by adding
	 * a `providerOrderOverride` on the component itself
	 * It can be either a string: `'cinema, fandango, movietickets, atom'`
	 * or an object: `{ cinema: 5, fandango: 2, movietickets: 3, atom: 4 }`
	 * warning: case-sensitive!
	 *
	 * @return {Array.<string>} array of provider names sorted by preference
	 */
	getSortedProviders() {
		const screening = this.props.activeScreening;
		const urls = screening && screening.urls;
		if (!urls) return [];
		let providers = Object.keys(urls);

		if (screening.disabledProviders) {
			providers = providers.filter(provider => screening.disabledProviders.indexOf(provider) === -1);
		}

		let order = this.providerOrderOverride;
		if (!order) {
			order = accessNested(this.props, 'pageData.ticketProvidersOrder');
			if (!order) return providers;

			if (typeof order === 'string') {
				order = order.split(/[|/,;]/).map(e => e.trim());
			} else if (typeof order === 'object' && !Array.isArray(order)) {
				order = Object.keys(order).sort((a, b) => order[a] - order[b]);
			}
		}

		// Sort the providers:
		// (1) reverse the order so that unknown items (indexOf === -1) are put at the end
		// (2) use sort() to sort providers in desc order
		// (3) reverse the order again to retain the initial order (in case `providerOrderOverride` is used)
		order.reverse();
		providers.sort((a, b) => order.indexOf(b) - order.indexOf(a));
		order.reverse();

		return providers;
	}

	render(props) {
		const {
			theater,         // Theater - the theater of the selected screening
			date,            // Date - the select screening date
			activeScreening, // Screening - the screening currently selected
			filters,
			appData,
			pageData
		} = props;

		const screening = activeScreening;
		const { copy } = appData;


		let { ticketProviders } = appData.pages.showtimes.data;
		const { ctaCopy } = appData.pages.showtimes.data; // why not pageData?

		if (pageData
			&& pageData.ticketProviders
			&& typeof pageData.ticketProviders === 'object'
			&& pageData.ticketProviders.override
		) {
			ticketProviders = pageData.ticketProviders;
		}

		let time = '';
		let format = '';
		let activeProviders = [];

		if (screening) {
			activeProviders = this.getSortedProviders();

			if (screening.cta || !screening.time) {
				const ctaVal = ctaCopy[screening.cta || 0];
				time = copy[ctaVal] || ctaVal;
			} else {
				const timeNumbers = formatDateTime(screening.datetime, copy.$TIME_FORMAT, copy.$DATETIME_LOCALE);
				let timePeriod = formatDateTime(screening.datetime, copy.$TIME_PERIOD, copy.$DATETIME_LOCALE);

				if (screening.hour <= 2 && +screening.stDate.date === +date) {
					const nextDay = new Date(screening.datetime);
					nextDay.setDate(nextDay.getDate() + 1);
					timePeriod += ' (' + formatDateTime(nextDay, copy.$DAY_FORMAT, copy.$DATETIME_LOCALE) + ')';
				}

				time = [timeNumbers, (<span class={s.period}>{timePeriod}</span>)];
				format = accessNested(screening, 'movie.format.label', '');
			}
		}

		const getOffer = (offerVal) => {
			if (!offerVal) return undefined;
			if (typeof offerVal === 'string') return copy[offerVal];

			const checkContext = (o) => {
				if (!o || !o.copy) return false;
				if (o.format && screening.movie.format.slug !== o.format) return false;
				if (o.timeOfDay && screening.timeOfDay.id !== o.timeOfDay) return false;
				if (o.date && screening.stDate.ddmmyyyy !== o.date) return false;
				if (o.nameContains && theater.name.indexOf(o.nameContains) === -1) return false;
				return true;
			};

			if (checkContext(offerVal)) {
				return copy[offerVal.copy];
			} else {
				const firstMatch = Object.keys(offerVal).find(k => checkContext(offerVal[k]));
				return firstMatch && copy[offerVal[firstMatch].copy];
			}
		};

		const withOffer = [];
		const withoutOffer = [];

		if (screening) {
			// let activeProviders = theater.providers;
			const providerFilter = accessNested(filters, 'ticketProvider.current', []).map(e => e.id);
			if (providerFilter.length) {
				const filtered = activeProviders.filter(provider => providerFilter.indexOf(provider) !== -1);
				// Only filter if we have at least a result (don't show an empty interstitial)
				if (filtered.length) activeProviders = filtered;
			}

			activeProviders.forEach(provider => {
				const isCinema = provider === 'cinema'; // whether the provider is the cinema itself
				let offer;

				if (isCinema) {
					if (theater.exhibitor && theater.exhibitor.domain) {
						const chainOffer = getOffer(accessNested(ticketProviders, ['cinema', 'chainOffers', theater.exhibitor.domain]));
						if (chainOffer) offer = chainOffer;
					}
					if (theater.website) {
						const cinemaOffer = getOffer(accessNested(ticketProviders, ['cinema', 'cinemaOffers', theater.website]));
						if (cinemaOffer) offer = cinemaOffer;
					}
				} else {
					offer = getOffer(ticketProviders[provider] && ticketProviders[provider].offer);
				}

				const label = copy[isCinema ? '$DIRECT_FROM_CINEMA' : '$' + provider.toUpperCase()] || provider;

				const bowTie = provider === 'cinema' && theater.logo === 'bowtiecinemas.com.png' ? s.bowTie : '';

				const logo = offer && !isCinema
					? <div class={s.providerLogo} style={{ backgroundImage: `url(${appData.urls.roots.sharedImages}/logos/ticketproviders/fullsize/${appData.styling.logoShade}/${provider}.png)` }}></div>
					: [
						<img class={joinClasses(s.providerLogoSmall, bowTie)} alt={provider} src={`${appData.urls.roots.sharedImages}/logos/${isCinema ? '' : 'ticketproviders/'}${appData.styling.interstitialShade || appData.styling.logoShade}/${isCinema ? theater.logo : provider + '.png'}`} />,
						<p class={s.label}>{label}</p>
					];
				const classes = joinClasses('provider', 'providerButton', provider, s.provider, s[provider], s['h' + screening.hour], s['m' + screening.minute], s['d' + formatDateTime(screening.stDate.date, 'ddMM')]);

				let url = screening.urls[provider];

				if (provider === 'fandango' && pageData.forceComputeFandangoUrls) {
					const mid = pageData.customFandangoMovieId || screening.movie.fandango;
					if (mid) {
						const formattedDate = formatDateTime(screening.datetime, 'MM\\-dd\\-yyyy\\+HH\\:mm');
						url = 'http://www.fandango.com/redirect.aspx?&mid=' + mid + '&a=&tid=' + theater.tms_id + '&dte=0&date=' + formattedDate;
					}
				}
				if (provider === 'atom' && pageData.forceComputeAtomUrls) {
					if (pageData.customAtomUrl) {
						url = pageData.customAtomUrl;
					} else {
						const mid = pageData.customAtomMovieId || screening.movie.atom;
						if (mid) {
							const formattedDate = formatDateTime(screening.datetime, 'MM\\-dd\\-yyyy\\+HH\\:mm');
							url = 'https://www.atomtickets.com/checkout/redirect?venueId=' + theater.atom + '&productionId=' + mid + '&showtime=' + formattedDate + screening.tz_offset;
						}
					}
				}

				// Create provider buttons & links
				if (isCinema && isDirectTicketingEnabled(theater)) {
					// Direct ticketing is active: simple button to in-house ticketing
					(offer ? withOffer : withoutOffer).push(
						<button
							class={classes}
							name={`ticket_link_${provider}`}
							onClick={this.selectDirectTicketing}
						>
							{logo}
							{offer && <span class={s.offer}>{textToJsxArray(offer)}</span>}
						</button>
					);
				} else {
					(offer ? withOffer : withoutOffer).push(
						<Link
							class={classes}
							name={`ticket_link_${provider}`}
							rel="external"
							target="_blank"
							href={url}
							extraOnClick={this.selectProvider.bind(this, provider, !!offer, url)}
							renderCustomChild
						>
							{logo}
							{offer && <span class={s.offer}>{textToJsxArray(offer)}</span>}
						</Link>
					);
				}
			});
		}

		const divider = format ? <span class={s.timeFormatDivider} /> : '';

		return (
			<div class={s.panel}>
				<button class={s.back} onClick={this.back}>{copy.$CHANGE_TIME}</button>
				<h3 class={s.time}>
					<span class={s.formatTruncating} title={format}>{format}</span>
					{divider}
					<span>{time}</span>
				</h3>
				<h4 class={s.instruction}>{copy.$SELECT_TICKET_PROVIDER}</h4>
				<div class={s.providers}>
					<div class={s.withOffer}>
						{withOffer}
					</div>
					<div class={s.withoutOffer}>
						{withoutOffer}
					</div>
				</div>
			</div>
		);
	}

}
